import type {ReactNode} from "react";
import {isValidElement, useLayoutEffect, useRef, useState} from "react";
import {flushSync} from "react-dom";
import type {Pointer} from "../../../utils/interaction/Pointer";
import {
	RATIO_EPSILON,
	SPLITTER_V5_DRAG_HANDLE_WIDTH_PX,
	applyDragWithChainPropagation,
	buildSplitterLayoutGridTemplateColumns,
	calculateMainPanelWidth,
	computeNewFirstPanelFractionForRecalculate,
	computeRecalculatePaneSizingRow,
	constrainUnitRatiosToPanelBounds,
	getSplitterContentWidthPx,
	getSplitterDomTopologyAndStride,
	insertFirstPanelShare,
	mergePersistedLayoutAfterPointerUp,
	normalizePanelWidthsForPersist,
	parseSplitterLayoutV5,
	resolvePersistedRatios,
	stringifySplitterLayoutV5,
	sumDomDataWidths,
} from "./splitterLayoutMath";

/**
 * What this hook does
 * -------------------
 * The splitter is one row of columns: pane, drag handle, pane, drag handle, … This hook picks how wide each pane is
 * (as a fraction of the row), builds the column styles for that row, saves sizes in the browser, and handles dragging.
 * It does not lay out what is *inside* a pane (lists, headers, etc.)—that is each child’s job.
 *
 * Numbers and sizes:
 * Each pane has a width value that works like a slice of a pie: the slices are scaled so they add up to 1 (100%).
 * Each pane root can set `data-min-width`, `data-default-width`, and `data-max-width` so columns stay within those
 * pixel limits. When you drag, width moves from one pane to the next; if a pane hits a limit, extra movement can
 * move through other panes (see `applyDragWithChainPropagation` in splitterLayoutMath.ts).
 *
 * Drag math (two panes beside a handle):
 *   1) requestedDeltaRatio = pointerOffsetX / contentWidthPx
 *      `contentWidthPx` is the width left for panes after subtracting drag-handle columns (`calculateMainPanelWidth`).
 *   2) newWeightLeft = currentWeightLeft + requestedDeltaRatio; newWeightRight = currentWeightRight - requestedDeltaRatio
 *   3) Min and max pixel widths are turned into fractions of the row, then we keep each pane inside those bounds.
 *      The math file handles the “spill over to the next pane” case when a pane cannot move further.
 *
 * When layout runs again:
 * On window resize, focus, or when pane membership or sizing attributes change, we read sizes from the page and from saved data, apply min/max,
 * and update the column style string.
 *
 * Main names:
 * - `layoutTracks`: what we draw, left to right (panes and handles).
 * - `gridTemplateColumns`: the style string for column widths on the root grid element.
 * - Shared math lives in `splitterLayoutMath.ts`; this file connects React, reading sizes from the page, and pointer events.
 */

/** Marks each column in the left-to-right list as either a pane or a drag handle (for building the column styles). */
export type SplitterV5LayoutPaneSizingSegment = "splitterPane" | "splitterDragHandle";

export type SplitterV5LayoutPaneSizing = {
	element: ReactNode;
	key: string;
	width: number;
	minWidth: number;
	maxWidth: number;
	splitterLayoutSegment: SplitterV5LayoutPaneSizingSegment;
	originalRatio?: number;
};

/** One item in order from left to right: either a pane or a drag handle. */
export type SplitterV5LayoutTrack =
	| {readonly kind: "splitterPane"; readonly splitterPane: SplitterV5LayoutPaneSizing}
	| {
			readonly kind: "splitterDragHandle";
			readonly splitterLayoutKey: string;
			readonly splitterPaneIndex: number;
			readonly splitterDragHandleDataId: number;
	  };

type UseSplitterV5LayoutParams = {
	readonly children: ReactNode;
	readonly localStorageKey: string;
	readonly elementKeys: readonly string[];
};

/** First-pass bootstrap width for dock-main (matches dockable data-default-width). */
const DOCK_MAIN_FIRST_PANE_BOOTSTRAP_WIDTH_PX = 440;

/**
 * Always returns an array of children so each index lines up with `elementKeys` (React may pass one child or an array).
 */
function getPaneChildSlots(children: ReactNode): ReactNode[] {
	if (children == null) {
		return [];
	}
	return Array.isArray(children) ? children : [children];
}

/** Starts the splitter layout logic; see the overview at the top of this file. */
export function useSplitterV5Layout(params: UseSplitterV5LayoutParams) {
	const {children, localStorageKey, elementKeys} = params;

	const paneChildSlots = getPaneChildSlots(children);

	const [layoutTracks, setLayoutTracks] = useState<SplitterV5LayoutTrack[]>([]);
	const [activeDragHandlePaneIndex, setActiveDragHandlePaneIndex] = useState(-1);

	const savedBodyCursor = useRef("");
	const rootRef = useRef<HTMLDivElement>(null);
	const pointerMoveRef = useRef<((pointer: Pointer) => void) | null>(null);
	const layoutTracksRef = useRef(layoutTracks);
	const paneChildSlotsRef = useRef(paneChildSlots);
	const previousPaneCountRef = useRef<number | null>(null);
	const recalculateLayoutRef = useRef<(() => void) | null>(null);
	/** Saved id from `requestAnimationFrame` so we can cancel it; used to run layout again when sizes are not ready yet. */
	const layoutRetryRafIdRef = useRef<number | null>(null);
	const domRetryCountRef = useRef(0);
	/** Last good min/max pixel widths per pane id; used when sizes are not ready yet so column width does not jump. */
	const paneDomMetricsByKeyRef = useRef<Map<string, {minWidth: number; maxWidth: number}>>(new Map());

	useLayoutEffect(() => {
		paneChildSlotsRef.current = paneChildSlots;
		layoutTracksRef.current = layoutTracks;
	});

	/**
	 * Changes when any pane slot gains or loses real content, not only when React passes a new `children` object.
	 * For each slot we record the stable id from `elementKeys` if there is a real child, else nothing. Slots are joined
	 * with `|`. When this string changes, we run layout again (for example a docked panel opens or closes).
	 */
	const paneSlotOccupancySignature = paneChildSlots
		.map((child, index) => (child != null && child !== false && isValidElement(child) ? elementKeys[index] : ""))
		.join("|");

	const gridTemplateColumns = buildSplitterLayoutGridTemplateColumns(tracksToFlatPaneSizing(layoutTracks), SPLITTER_V5_DRAG_HANDLE_WIDTH_PX);

	/** When the user grabs a handle: read on-screen widths so the drag matches what they see. */
	const onHandlePointerDown = () => {
		setLayoutTracks((previousTracks) => {
			const gridElementChildren = Array.from(rootRef.current?.children ?? []);

			if (gridElementChildren.length === 0) {
				return previousTracks;
			}

			const measuredPaneWidthsPx: number[] = [];
			let paneCount = 0;

			for (let i = 0; i < previousTracks.length; i++) {
				if (previousTracks[i].kind !== "splitterPane") {
					continue;
				}
				const measuredWidth = gridElementChildren[i]?.getBoundingClientRect().width ?? 0;
				measuredPaneWidthsPx.push(Math.max(0, measuredWidth));
				paneCount++;
			}

			if (paneCount < 2) {
				return previousTracks;
			}

			const totalMeasuredWidth = measuredPaneWidthsPx.reduce((sum, width) => sum + width, 0);

			if (totalMeasuredWidth <= RATIO_EPSILON) {
				return previousTracks;
			}

			let paneCursor = 0;

			return previousTracks.map((track) => {
				if (track.kind !== "splitterPane") {
					return track;
				}
				const normalizedWidth = measuredPaneWidthsPx[paneCursor++] / totalMeasuredWidth;
				return {
					kind: "splitterPane" as const,
					splitterPane: {...track.splitterPane, width: normalizedWidth, originalRatio: normalizedWidth},
				};
			});
		});
	};

	/** When the user releases the handle: normalize widths, merge into saved layout, save in the browser. */
	const onHandlePointerUp = () => {
		setLayoutTracks((previousTracks) => {
			const nextTracks = previousTracks.map((track) =>
				track.kind === "splitterPane"
					? {
							kind: "splitterPane" as const,
							splitterPane: {...track.splitterPane, originalRatio: track.splitterPane.width},
						}
					: track,
			);

			const paneSizingRows: SplitterV5LayoutPaneSizing[] = [];

			for (const track of nextTracks) {
				if (track.kind === "splitterPane") {
					paneSizingRows.push(track.splitterPane);
				}
			}
			normalizePanelWidthsForPersist(paneSizingRows);
			const persistedLayout = parseSplitterLayoutV5(localStorage.getItem(localStorageKey));

			const mergedLayout = mergePersistedLayoutAfterPointerUp(
				persistedLayout,
				paneSizingRows.map((r) => r.width),
			);

			localStorage.setItem(localStorageKey, stringifySplitterLayoutV5(mergedLayout));

			return nextTracks;
		});
	};

	useLayoutEffect(() => {
		domRetryCountRef.current = 0;

		pointerMoveRef.current = (pointer: Pointer) => {
			flushSync(() => {
				const dragHandleDataId = parseInt(pointer.currentTarget.getAttribute("data-id") ?? "", 10);

				setLayoutTracks((previousTracks) => {
					const flatPaneSizing = tracksToFlatPaneSizing(previousTracks);
					const paneCount = flatPaneSizing.filter((row) => row?.splitterLayoutSegment === "splitterPane").length;
					const contentWidthPx = calculateMainPanelWidth(rootRef, paneCount);
					const requestedDeltaRatio = pointer.offsetX / contentWidthPx;

					const paneFlatIndices: number[] = [];
					const paneWeights: number[] = [];
					const paneMinWidthsPx: number[] = [];
					const paneMaxWidthsPx: number[] = [];

					for (let i = 0; i < flatPaneSizing.length; i++) {
						const row = flatPaneSizing[i];

						if (row?.splitterLayoutSegment === "splitterPane") {
							paneFlatIndices.push(i);
							paneWeights.push(row.originalRatio ?? row.width);
							paneMinWidthsPx.push(row.minWidth);
							paneMaxWidthsPx.push(row.maxWidth ?? 0);
						}
					}

					const leftFlatIndex = dragHandleDataId - 1;
					const rightFlatIndex = dragHandleDataId + 1;
					const leftPanePosition = paneFlatIndices.findIndex((flatIdx) => flatIdx === leftFlatIndex);
					const rightPanePosition = paneFlatIndices.findIndex((flatIdx) => flatIdx === rightFlatIndex);

					let nextFlatPaneSizing: SplitterV5LayoutPaneSizing[];

					if (leftPanePosition >= 0 && rightPanePosition === leftPanePosition + 1 && paneWeights.length >= 2) {
						applyDragWithChainPropagation(
							paneWeights,
							paneMinWidthsPx,
							paneMaxWidthsPx,
							contentWidthPx,
							leftPanePosition,
							rightPanePosition,
							requestedDeltaRatio,
						);
						const nextWeightByFlatIndex = new Map<number, number>();

						for (let i = 0; i < paneFlatIndices.length; i++) {
							nextWeightByFlatIndex.set(paneFlatIndices[i], paneWeights[i]);
						}

						nextFlatPaneSizing = flatPaneSizing.map((flatRow, flatIndex) => {
							if (flatRow?.splitterLayoutSegment !== "splitterPane") {
								return {...flatRow};
							}

							const nextWeight = nextWeightByFlatIndex.get(flatIndex);

							return nextWeight !== undefined ? {...flatRow, width: nextWeight} : {...flatRow};
						});
					} else {
						nextFlatPaneSizing = flatPaneSizing.map((flatRow, flatIndex) => {
							const baseRatio = flatRow.originalRatio ?? flatRow.width;

							if (flatIndex === leftFlatIndex) {
								return {...flatRow, width: baseRatio + requestedDeltaRatio};
							} else if (flatIndex === rightFlatIndex) {
								return {...flatRow, width: baseRatio - requestedDeltaRatio};
							}

							return {...flatRow};
						});
					}

					return flatPaneSizingToTracks(previousTracks, nextFlatPaneSizing);
				});
			});
		};

		/** Recomputes column sizes from props, on-screen attributes, and saved data. Skips while the user is dragging. */
		const recalculateLayout = () => {
			if (activeDragHandlePaneIndex !== -1) {
				return;
			}

			const persistedLayout = parseSplitterLayoutV5(localStorage.getItem(localStorageKey));

			const latestPaneChildSlots = paneChildSlotsRef.current;
			const validChildren = latestPaneChildSlots.filter((slot) => slot != null && slot !== false && isValidElement(slot));
			const previousPaneCount = previousPaneCountRef.current;
			const paneCountChanged = previousPaneCount !== null && previousPaneCount !== validChildren.length;
			const contentWidthPx = getSplitterContentWidthPx(rootRef, validChildren.length);
			const gridDomChildren = Array.from(rootRef.current?.children ?? []);

			const {domTopologyMatches, stride} = getSplitterDomTopologyAndStride(validChildren.length, gridDomChildren.length);

			const sumOfDefaultWidths = sumDomDataWidths(gridDomChildren, "default");
			const sumOfMinWidths = sumDomDataWidths(gridDomChildren, "min");

			let widthOffset = 0;

			const calculatedPaneSizing: SplitterV5LayoutPaneSizing[] = [];
			for (let i = 0; i < latestPaneChildSlots.length; ++i) {
				const elementKey = elementKeys[i];
				const validChildMaybe = latestPaneChildSlots[i];

				if (validChildMaybe != null && validChildMaybe !== false && isValidElement(validChildMaybe)) {
					let domChildMaybe: Element | undefined;

					if (validChildren.length > 1) {
						const indexOfValidChild = validChildren.findIndex((vc) => vc === validChildMaybe);
						domChildMaybe = gridDomChildren[indexOfValidChild * stride];
					}

					const cachedMetrics = paneDomMetricsByKeyRef.current.get(elementKey);

					const row = computeRecalculatePaneSizingRow({
						validPaneCount: validChildren.length,
						domTopologyMatches,
						cachedMetrics,
						domChildMaybe,
						splitterContentWidthPx: contentWidthPx,
						sumOfDefaultWidths,
						sumOfMinWidths,
						widthOffset,
					});

					widthOffset = row.nextWidthOffset;

					calculatedPaneSizing.push({
						key: elementKey,
						// Content comes directly from the latest props when rendering; sizing
						// updates must not delay new pane contents until a second commit.
						element: null,
						minWidth: row.minWidth,
						maxWidth: row.maxWidth,
						width: row.finalWidthRatio,
						splitterLayoutSegment: "splitterPane",
						originalRatio: row.finalWidthRatio,
					});
				}
			}

			let newFirstPanelFraction: number | undefined;
			if (calculatedPaneSizing.length >= 2 && validChildren.length >= 2 && contentWidthPx > 0) {
				const firstPane = calculatedPaneSizing[0];

				newFirstPanelFraction = computeNewFirstPanelFractionForRecalculate({
					validPaneCount: validChildren.length,
					splitterContentWidthPx: contentWidthPx,
					firstPaneMinWidth: firstPane.minWidth,
					firstPaneMaxWidth: firstPane.maxWidth,
					lastByPaneCountForN: persistedLayout.lastByPaneCount?.[validChildren.length],
					domTopologyMatches,
					firstDomChildMaybe: gridDomChildren[0 * stride],
				});
			}

			const previousFirstPaneTrack = layoutTracksRef.current.find((row) => row.kind === "splitterPane");
			const previousFirstPaneKey = previousFirstPaneTrack?.kind === "splitterPane" ? previousFirstPaneTrack.splitterPane.key : undefined;
			const currentFirstPaneKey = calculatedPaneSizing[0]?.key;

			const allowRemoveFirstProjection =
				paneCountChanged &&
				previousPaneCount !== null &&
				previousPaneCount === validChildren.length + 1 &&
				previousFirstPaneKey !== undefined &&
				currentFirstPaneKey !== undefined &&
				previousFirstPaneKey !== currentFirstPaneKey;

			const resolvedRatios = resolvePersistedRatios(calculatedPaneSizing.length, persistedLayout, {
				newFirstPanelFraction,
				allowRemoveFirstProjection,
			});
			const fallbackRatios = calculatedPaneSizing.map((c) => c.width);
			const canBootstrapDockMainFirstPass =
				localStorageKey.includes("dock-main") &&
				paneCountChanged &&
				previousPaneCount === 1 &&
				validChildren.length === 2 &&
				!domTopologyMatches &&
				resolvedRatios == null &&
				contentWidthPx > 0;
			const dockMainBootstrapRatios = canBootstrapDockMainFirstPass
				? insertFirstPanelShare([1], DOCK_MAIN_FIRST_PANE_BOOTSTRAP_WIDTH_PX / contentWidthPx)
				: null;
			const initialRatios =
				resolvedRatios && resolvedRatios.length === calculatedPaneSizing.length
					? resolvedRatios
					: dockMainBootstrapRatios && dockMainBootstrapRatios.length === calculatedPaneSizing.length
						? dockMainBootstrapRatios
						: fallbackRatios;

			// During 1->2 dock-main transition, DOM topology can still be stale for one pass. Skip constraint in that pass
			// so we do not clamp with old-pane min/max and render a temporary "wrong" width before the steady pass.
			const shouldSkipConstrainForBootstrap = canBootstrapDockMainFirstPass;
			if (initialRatios.length === calculatedPaneSizing.length && !shouldSkipConstrainForBootstrap) {
				const minPx = calculatedPaneSizing.map((c) => c.minWidth);
				const maxPx = calculatedPaneSizing.map((c) => c.maxWidth);
				const constrainedRatios = constrainUnitRatiosToPanelBounds(initialRatios, minPx, maxPx, contentWidthPx);

				for (let i = 0; i < calculatedPaneSizing.length; i++) {
					calculatedPaneSizing[i].width = constrainedRatios[i];
					calculatedPaneSizing[i].originalRatio = constrainedRatios[i];
				}
			} else if (initialRatios.length === calculatedPaneSizing.length) {
				for (let i = 0; i < calculatedPaneSizing.length; i++) {
					calculatedPaneSizing[i].width = initialRatios[i];
					calculatedPaneSizing[i].originalRatio = initialRatios[i];
				}
			}

			if (domTopologyMatches) {
				for (const pane of calculatedPaneSizing) {
					paneDomMetricsByKeyRef.current.set(pane.key, {minWidth: pane.minWidth, maxWidth: pane.maxWidth});
				}
			}

			let dragHandleCounter = 1;
			const nextTracks: SplitterV5LayoutTrack[] = [];

			for (let index = 0; index < calculatedPaneSizing.length; index++) {
				nextTracks.push({kind: "splitterPane", splitterPane: calculatedPaneSizing[index]});

				if (index !== calculatedPaneSizing.length - 1) {
					nextTracks.push({
						kind: "splitterDragHandle",
						splitterLayoutKey: `key_${index}`,
						splitterPaneIndex: index,
						splitterDragHandleDataId: dragHandleCounter,
					});

					dragHandleCounter += 2;
				}
			}

			setLayoutTracks((previousTracks) => (haveSameTrackSizes(previousTracks, nextTracks) ? previousTracks : nextTracks));

			previousPaneCountRef.current = validChildren.length;

			if (domTopologyMatches) {
				domRetryCountRef.current = 0;
			} else if (validChildren.length > 0 && activeDragHandlePaneIndex === -1) {
				if (domRetryCountRef.current < 8) {
					domRetryCountRef.current += 1;

					if (layoutRetryRafIdRef.current == null) {
						layoutRetryRafIdRef.current = requestAnimationFrame(() => {
							layoutRetryRafIdRef.current = null;
							recalculateLayoutRef.current?.();
						});
					}
				}
			}
		};

		recalculateLayoutRef.current = recalculateLayout;
		recalculateLayout();
		const onResize = () => recalculateLayout();
		window.addEventListener("resize", onResize);

		const resizeObserver =
			typeof ResizeObserver !== "undefined"
				? new ResizeObserver(() => {
						recalculateLayout();
					})
				: null;
		if (resizeObserver && rootRef.current) {
			resizeObserver.observe(rootRef.current);
		}

		// Watch only pane roots, never their editor subtrees. A pane component can
		// change its constraints without changing which child slots are occupied.
		const constraintAttributes = ["data-min-width", "data-max-width", "data-default-width", "hidden", "class", "style"];
		let observedConstraintSignature = "";
		const getPaneRoots = () =>
			Array.from(rootRef.current?.children ?? []).filter((_child, index) => layoutTracksRef.current[index]?.kind === "splitterPane");
		const readConstraintSignature = () =>
			JSON.stringify(getPaneRoots().map((child) => constraintAttributes.map((attribute) => child.getAttribute(attribute))));
		const observePaneConstraints = () => {
			constraintObserver?.disconnect();
			if (rootRef.current) {
				constraintObserver?.observe(rootRef.current, {childList: true});
				for (const child of getPaneRoots()) {
					constraintObserver?.observe(child, {attributes: true, attributeFilter: constraintAttributes});
				}
			}
		};
		const constraintObserver =
			typeof MutationObserver !== "undefined"
				? new MutationObserver((records) => {
						if (records.some((record) => record.type === "childList")) {
							observePaneConstraints();
						}
						const signature = readConstraintSignature();
						if (signature !== observedConstraintSignature) {
							observedConstraintSignature = signature;
							recalculateLayout();
						}
					})
				: null;
		observedConstraintSignature = readConstraintSignature();
		observePaneConstraints();

		window.addEventListener("focus", recalculateLayout);

		return () => {
			window.removeEventListener("resize", onResize);
			window.removeEventListener("focus", recalculateLayout);
			resizeObserver?.disconnect();
			constraintObserver?.disconnect();

			if (layoutRetryRafIdRef.current != null) {
				cancelAnimationFrame(layoutRetryRafIdRef.current);
				layoutRetryRafIdRef.current = null;
			}
		};
	}, [paneSlotOccupancySignature, activeDragHandlePaneIndex, localStorageKey, elementKeys]);

	const currentChildrenByKey = new Map(paneChildSlots.map((child, index) => [elementKeys[index], child]));
	const currentLayoutTracks = layoutTracks.map(
		(track): SplitterV5LayoutTrack =>
			track.kind === "splitterPane"
				? {...track, splitterPane: {...track.splitterPane, element: currentChildrenByKey.get(track.splitterPane.key)}}
				: track,
	);

	return {
		rootRef,
		layoutTracks: currentLayoutTracks,
		gridTemplateColumns,
		activeDragHandlePaneIndex,
		setActiveDragHandlePaneIndex,
		savedBodyCursor,
		onHandlePointerDown,
		onHandlePointerUp,
		onHandlePointerMove: (pointer: Pointer) => pointerMoveRef.current?.(pointer),
	};
}

/** Ignore React element identity: only a geometry change needs another layout commit. */
function haveSameTrackSizes(previous: SplitterV5LayoutTrack[], next: SplitterV5LayoutTrack[]): boolean {
	return (
		previous.length === next.length &&
		previous.every((track, index) => {
			const other = next[index];
			if (track.kind === "splitterPane" && other.kind === "splitterPane") {
				const pane = track.splitterPane;
				const nextPane = other.splitterPane;
				return (
					pane.key === nextPane.key &&
					pane.width === nextPane.width &&
					pane.minWidth === nextPane.minWidth &&
					pane.maxWidth === nextPane.maxWidth &&
					pane.originalRatio === nextPane.originalRatio
				);
			}
			return (
				track.kind === "splitterDragHandle" &&
				other.kind === "splitterDragHandle" &&
				track.splitterLayoutKey === other.splitterLayoutKey &&
				track.splitterPaneIndex === other.splitterPaneIndex &&
				track.splitterDragHandleDataId === other.splitterDragHandleDataId
			);
		})
	);
}

/** Turns the track list into one left-to-right list for building column styles and for drag handling. */
function tracksToFlatPaneSizing(tracks: SplitterV5LayoutTrack[]): SplitterV5LayoutPaneSizing[] {
	const out: SplitterV5LayoutPaneSizing[] = [];
	for (const r of tracks) {
		if (r.kind === "splitterPane") {
			out.push(r.splitterPane);
		} else {
			out.push({
				key: r.splitterLayoutKey,
				element: null,
				width: SPLITTER_V5_DRAG_HANDLE_WIDTH_PX,
				minWidth: SPLITTER_V5_DRAG_HANDLE_WIDTH_PX,
				maxWidth: 0,
				splitterLayoutSegment: "splitterDragHandle",
			});
		}
	}
	return out;
}

/** Puts new pane widths back into the existing track list; leaves drag-handle entries as they were. */
function flatPaneSizingToTracks(prevTracks: SplitterV5LayoutTrack[], flat: SplitterV5LayoutPaneSizing[]): SplitterV5LayoutTrack[] {
	let flatIndex = 0;
	return prevTracks.map((r) => {
		const updated = flat[flatIndex++];
		if (r.kind === "splitterDragHandle") {
			return r;
		}
		return {
			kind: "splitterPane" as const,
			splitterPane: {...r.splitterPane, width: updated.width, originalRatio: updated.originalRatio},
		};
	});
}
